/**
 * @fileoverview API Interceptors Module
 *
 * This module provides request and response interceptors for Axios instances.
 * It handles:
 * - Cached header management (token, orgUUID, userPool) with 1-minute TTL
 * - Immediate cache invalidation on token update via 'auth-token-updated' CustomEvent
 * - Automatic 401 error handling with token refresh
 * - Proactive token renewal via X-New-Access-Token header
 * - Cache-control header injection
 *
 * @module services/api/interceptors
 */

import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

import { CACHE_TTL } from './config';
import { CookieManager } from '../../utils/cookieManager';
import { logger } from 'utils/logger';

// ============================================================================
// Types
// ============================================================================

/**
 * Cached headers structure for performance optimization
 *
 * @interface CachedHeaders
 */
interface CachedHeaders {
  token: string | null;
  orgUUID: string | null;
  userPool: string | null;
  lastUpdate: number;
}

/**
 * Stored interceptor IDs for cleanup
 *
 * @interface InterceptorIds
 */
interface InterceptorIds {
  request: number;
  response: number;
}

/**
 * Extended Axios request config with retry flag
 */
interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Function type for updating headers across API instances
 */
export type UpdateHeadersFunction = (token: string, orgUUID?: string, userPool?: string) => void;

// ============================================================================
// Module State
// ============================================================================

/**
 * Map of instance keys to their interceptor IDs for cleanup
 * @private
 */
const interceptorIds = new Map<string, InterceptorIds>();

// ============================================================================
// Header Cache Factory
// ============================================================================

/**
 * Accessor object returned by createHeaderCache.
 * Provides get() for reading cached headers and invalidate() for forcing a refresh.
 */
interface HeaderCacheAccessor {
  get: () => CachedHeaders;
  invalidate: () => void;
}

/**
 * Creates a cached header accessor for performance optimization
 *
 * Headers are cached for CACHE_TTL (1 minute) to minimize localStorage reads.
 * This significantly reduces performance overhead for high-frequency API calls.
 * The returned object exposes an `invalidate()` method so the cache can be
 * force-refreshed immediately when the auth token changes (e.g. after a
 * proactive renewal via X-New-Access-Token or a 401 refresh flow).
 *
 * @returns HeaderCacheAccessor with get() and invalidate() methods
 *
 * @example
 * const headerCache = createHeaderCache();
 * const { token, orgUUID, userPool } = headerCache.get();
 * // After a token update:
 * headerCache.invalidate();
 */
export const createHeaderCache = (): HeaderCacheAccessor => {
  let cachedHeaders: CachedHeaders = {
    token: null,
    orgUUID: null,
    userPool: null,
    lastUpdate: 0,
  };

  const refreshFromStorage = (): CachedHeaders => {
    cachedHeaders = {
      token: localStorage.getItem('AUTH_TOKEN'),
      orgUUID: localStorage.getItem('x-organization-uuid'),
      userPool: localStorage.getItem('x-cognito-userpool'),
      lastUpdate: Date.now(),
    };
    return cachedHeaders;
  };

  return {
    get: () => {
      const now = Date.now();
      if (now - cachedHeaders.lastUpdate > CACHE_TTL) {
        return refreshFromStorage();
      }
      return cachedHeaders;
    },
    invalidate: () => {
      refreshFromStorage();
    },
  };
};

// ============================================================================
// Request Interceptor
// ============================================================================

/**
 * Sets up request interceptor with header validation and injection
 *
 * The request interceptor:
 * - Uses cached headers (1-minute TTL) to minimize localStorage reads
 * - Validates and adds required headers: Authorization, X-Organization-UUID, X-Cognito-UserPool
 * - Sets Content-Type for JSON requests (except FormData)
 * - Removes headers for refresh-token endpoint to avoid CORS preflight
 *
 * @param instance - The Axios instance to attach the interceptor to
 * @param key - Unique identifier for this instance (used for logging)
 * @returns The interceptor ID for later cleanup
 *
 * @example
 * const requestInterceptorId = setupRequestInterceptor(axiosInstance, 'orchestrator');
 */
export const setupRequestInterceptor = (instance: AxiosInstance, key: string): number => {
  const headerCache = createHeaderCache();

  // Listen for token updates to invalidate cache immediately
  const handleTokenUpdate = () => headerCache.invalidate();
  window.addEventListener('auth-token-updated', handleTokenUpdate);

  return instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Ensure headers object exists (defensive check)
      // Axios v1.x+ guarantees headers exists, but we do defensive check
      config.headers = config.headers || ({} as any);

      // --- VALIDATION WITH CACHE ---
      // Use cache to minimize localStorage access
      const { token, orgUUID, userPool } = headerCache.get();

      // Validate Authorization (fallback if not set)
      if (!config.headers.Authorization && !config.headers.authorization) {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // Log for debug, but don't block (endpoint may not need it)
          logger.warn(`[ApiService:${key}] API Call without token:`, config.url);
        }
      }

      // Validate X-Organization-UUID (fallback)
      if (!config.headers['X-Organization-UUID'] && !config.headers['x-organization-uuid']) {
        if (orgUUID) {
          config.headers['X-Organization-UUID'] = orgUUID;
        } else {
          logger.warn(`[ApiService:${key}] API Call without organization UUID:`, config.url);
        }
      }

      // Validate X-Cognito-UserPool (fallback)
      if (!config.headers['X-Cognito-UserPool'] && !config.headers['x-cognito-userpool']) {
        if (userPool) {
          config.headers['X-Cognito-UserPool'] = userPool;
        } else {
          logger.warn(`[ApiService:${key}] API Call without cognito pool:`, config.url);
        }
      }

      // Content-Type for JSON (only if not FormData - avoids breaking multipart)
      if (['post', 'put', 'patch'].includes(config.method?.toLowerCase() || '')) {
        if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
          config.headers['Content-Type'] = 'application/json';
        }
      }

      // Existing logic for refresh-token (don't send Authorization/Content-Type)
      if (config.url?.includes('/auth/refresh-token')) {
        delete config.headers.Authorization;
        delete config.headers['Content-Type'];
        logger.debug('[ApiService] Headers removed for refresh-token (avoid preflight)');
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );
};

// ============================================================================
// Response Interceptor
// ============================================================================

/**
 * Sets up response interceptor with token refresh and error handling
 *
 * The response interceptor:
 * - Adds cache-control headers to prevent stale responses
 * - Detects X-New-Access-Token header for proactive token renewal
 * - Handles 401 errors with automatic token refresh via RefreshTokenService
 * - Logs errors for debugging
 *
 * @param instance - The Axios instance to attach the interceptor to
 * @param key - Unique identifier for this instance (used for logging and cookie debug)
 * @param updateHeadersFn - Function to update headers across all API instances
 * @returns The interceptor ID for later cleanup
 *
 * @example
 * const responseInterceptorId = setupResponseInterceptor(
 *   axiosInstance,
 *   'orchestrator',
 *   defaultsHeadersAxios
 * );
 */
export const setupResponseInterceptor = (
  instance: AxiosInstance,
  key: string,
  updateHeadersFn: UpdateHeadersFunction
): number => {
  return instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Detailed logging for cookie debugging on login
      if (key === 'orchestrator' && response.config.url?.includes('/auth/login')) {
        logger.debug('[ApiService] Debug cookies in response interceptor:', {
          url: response.config.url,
          status: response.status,
          headers: response.headers,
          'set-cookie-header': response.headers['set-cookie'],
          'Set-Cookie-header': response.headers['Set-Cookie'],
          'all-headers': Object.keys(response.headers),
        });
      }

      // Add cache control headers to prevent caching issues
      response.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      response.headers['Pragma'] = 'no-cache';
      response.headers['Expires'] = '0';

      // If backend proactively renewed the token, update Authorization
      const newTokenHeader = (response.headers as Record<string, string>)?.['x-new-access-token'];
      if (newTokenHeader) {
        try {
          // Update default headers of all instances
          const orgUUID = localStorage.getItem('x-organization-uuid') || undefined;
          const cognitoUserPool = localStorage.getItem('x-cognito-userpool') || undefined;
          updateHeadersFn(newTokenHeader, orgUUID, cognitoUserPool);

          // Mirror token in storage/cookie (debug/consistency)
          localStorage.setItem('AUTH_TOKEN', newTokenHeader);
          CookieManager.setAccessToken(newTokenHeader);

          // Dispatch event to invalidate header caches across all instances
          window.dispatchEvent(new CustomEvent('auth-token-updated'));

          // Light log for diagnostics
          logger.debug('[ApiService] X-New-Access-Token detected. Authorization updated.');
        } catch (e) {
          logger.warn('[ApiService] Failed to apply X-New-Access-Token:', { data: e });
        }
      }

      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableAxiosRequestConfig;

      // If it's a 401 error and not a refresh attempt
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        logger.debug(
          '[ApiService] Interceptor: 401 error detected, starting automatic refresh...',
          {
            url: originalRequest.url,
            method: originalRequest.method,
            hasRetry: originalRequest._retry,
            timestamp: new Date().toISOString(),
          }
        );

        try {
          // Try to refresh the token
          // Dynamic import to avoid circular dependencies
          const { RefreshTokenService } = await import('../refreshTokenService');
          logger.debug('[ApiService] RefreshTokenService imported successfully');

          const newToken = await RefreshTokenService.refreshAccessToken();

          if (newToken) {
            logger.debug('[ApiService] Token renewed successfully!', {
              newTokenLength: newToken.length,
              timestamp: new Date().toISOString(),
            });

            // Update authorization header
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            logger.debug('[ApiService] Authorization header updated, retrying...');

            // Retry the original request
            return instance(originalRequest);
          } else {
            logger.warn('[ApiService] Refresh returned null token');
          }
        } catch (refreshError) {
          logger.error('[ApiService] Critical failure renewing token:', {
            error: refreshError,
            message: refreshError instanceof Error ? refreshError.message : 'Unknown error',
            stack: refreshError instanceof Error ? refreshError.stack : undefined,
            timestamp: new Date().toISOString(),
          });

          // Complete cleanup was already done by RefreshTokenService
          // We just log that the interceptor detected the failure
          logger.debug('[ApiService] Complete cleanup already executed by RefreshTokenService');
        }
      } else if (error.response?.status === 401 && originalRequest) {
        logger.debug('[ApiService] 401 error ignored (already tried refresh or not retry)', {
          url: originalRequest.url,
          hasRetry: originalRequest._retry,
          timestamp: new Date().toISOString(),
        });
      }

      // Log the original error
      logger.error('API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
      });

      return Promise.reject(error);
    }
  );
};

// ============================================================================
// Interceptor Management
// ============================================================================

/**
 * Sets up both request and response interceptors for an Axios instance
 *
 * This is the main entry point for configuring interceptors. It:
 * - Attaches request interceptor with header validation
 * - Attaches response interceptor with token refresh
 * - Stores interceptor IDs for later cleanup
 *
 * @param instance - The Axios instance to configure
 * @param key - Unique identifier for this instance
 * @param updateHeadersFn - Function to update headers across all API instances
 *
 * @example
 * import { setupInterceptors } from './interceptors';
 * import { defaultsHeadersAxios } from '../apiService';
 *
 * const instance = axios.create({ baseURL: 'https://api.example.com' });
 * setupInterceptors(instance, 'orchestrator', defaultsHeadersAxios);
 */
export const setupInterceptors = (
  instance: AxiosInstance,
  key: string,
  updateHeadersFn: UpdateHeadersFunction
): void => {
  const requestId = setupRequestInterceptor(instance, key);
  const responseId = setupResponseInterceptor(instance, key, updateHeadersFn);
  interceptorIds.set(key, { request: requestId, response: responseId });
};

/**
 * Clears interceptors from an Axios instance
 *
 * Use this method to clean up interceptors before destroying an instance
 * or when you need to reconfigure interceptor behavior.
 *
 * @param instance - The Axios instance to clear interceptors from
 * @param key - The instance key used when setting up interceptors
 *
 * @example
 * // Clean up before reconfiguration
 * clearInterceptors(axiosInstance, 'orchestrator');
 */
export const clearInterceptors = (instance: AxiosInstance, key: string): void => {
  const ids = interceptorIds.get(key);
  if (ids) {
    instance.interceptors.request.eject(ids.request);
    instance.interceptors.response.eject(ids.response);
    interceptorIds.delete(key);
  }
};

/**
 * Gets the interceptor IDs for a specific instance
 *
 * Useful for debugging or advanced interceptor management.
 *
 * @param key - The instance key
 * @returns The interceptor IDs or undefined if not found
 *
 * @example
 * const ids = getInterceptorIds('orchestrator');
 * if (ids) {
 *   console.log('Request interceptor ID:', ids.request);
 *   console.log('Response interceptor ID:', ids.response);
 * }
 */
export const getInterceptorIds = (key: string): InterceptorIds | undefined => {
  return interceptorIds.get(key);
};

/**
 * Checks if an instance has interceptors configured
 *
 * @param key - The instance key
 * @returns True if interceptors are configured for this instance
 *
 * @example
 * if (hasInterceptors('orchestrator')) {
 *   console.log('Orchestrator has interceptors configured');
 * }
 */
export const hasInterceptors = (key: string): boolean => {
  return interceptorIds.has(key);
};
